<template>
  <div
    class="col-span-6 sm:col-span-3 xl:col-span-2 flex flex-col justify-end items-center"
    v-if="loading"
  >
    <LoadingIcon icon="three-dots" class="w-8 h-8" />
  </div>
  <div>
    <form class="validate-form" @submit.prevent="handleSave" v-if="!loading">
      <div class="intro-y flex flex-col sm:flex-row items-center mt-8">
        <UsersIcon />
        <h2 class="text-lg font-medium mr-auto">
          {{ $t('common.profile') }} - {{ admin.mail }}
        </h2>
        <div class="w-full sm:w-auto flex mt-4 sm:mt-0">
          <button type="submit" class="btn btn-primary shadow-md flex items-center">
            <SaveIcon class="w-4 h-4 mr-2" /> {{ $t('common.save') }}
          </button>
        </div>
      </div>
      <div class="pos intro-y grid grid-cols-12 gap-5 mt-5">
        <!-- BEGIN: Post Info -->
        <div class="col-span-12">
          <div class="intro-y box p-5">
            <div class="mt-3">
              <label for="name" class="form-label w-full flex flex-col sm:flex-row">
                {{ $t('common.personName') }}
                <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                  {{ $t('form.required') }},
                  {{ $t('form.constraint.gte2ch') }}
                </span>
              </label>
              <input
                id="name"
                v-model.trim="validate.name.$model"
                type="text"
                name="name"
                class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13"
                :class="{ 'border-theme-6': validate.name.$error }"
                placeholder="John"
              />
              <template v-if="validate.name.$error">
                <div
                  v-for="(error, index) in validate.name.$errors"
                  :key="index"
                  class="text-theme-6 mt-2"
                >
                  {{ $t(error.$message) }}
                </div>
              </template>
            </div>
            <div class="mt-3">
              <label for="lastname" class="form-label w-full flex flex-col sm:flex-row">
                {{ $t('common.personLastname') }}
                <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                  {{ $t('form.required') }},
                  {{ $t('form.constraint.gte2ch') }}
                </span>
              </label>
              <input
                id="lastname"
                v-model.trim="validate.lastname.$model"
                type="text"
                name="lastname"
                class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13"
                :class="{ 'border-theme-6': validate.lastname.$error }"
                placeholder="Doe"
              />
              <template v-if="validate.lastname.$error">
                <div
                  v-for="(error, index) in validate.lastname.$errors"
                  :key="index"
                  class="text-theme-6 mt-2"
                >
                  {{ $t(error.$message) }}
                </div>
              </template>
            </div>
            <div class="mt-3">
              <label for="password" class="form-label w-full flex flex-col sm:flex-row">
                {{ $t('common.password') }}
                <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                  {{ $t('form.required') }},
                  {{ $t('form.constraint.gte6ch') }}
                </span>
              </label>
              <input
                id="password"
                v-model.trim="validate.password.$model"
                type="password"
                name="password"
                class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13"
                :class="{ 'border-theme-6': validate.password.$error }"
                placeholder="Secret password"
              />
              <template v-if="validate.password.$error">
                <div
                  v-for="(error, index) in validate.password.$errors"
                  :key="index"
                  class="text-theme-6 mt-2"
                >
                  {{ $t(error.$message) }}
                </div>
              </template>
            </div>
          </div>
        </div>
        <!-- END: Post Info -->
      </div>
    </form>
  </div>
  <!-- BEGIN: Success Notification Content -->
  <div id="success-notification-content" class="toastify-content hidden flex">
    <CheckCircleIcon class="text-theme-9" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ $t('profile.edited') }}!</div>
      <!-- <div class="text-gray-600 mt-1">
        Please check your e-mail for further info!
      </div> -->
    </div>
  </div>
  <!-- END: Success Notification Content -->
  <!-- BEGIN: Failed Notification Content -->
  <div id="failed-notification-content" class="toastify-content hidden flex">
    <XCircleIcon class="text-theme-6" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ $t('profile.notEdited') }}!</div>
      <!-- <div class="text-gray-600 mt-1">Please check the fileld form.</div> -->
    </div>
  </div>
  <!-- END: Failed Notification Content -->
  <!-- BEGIN: Failed Notification Content -->
  <div id="failed-load-notification-content" class="toastify-content hidden flex">
    <XCircleIcon class="text-theme-6" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ $t('profile.notLoaded') }}!</div>
      <!-- <div class="text-gray-600 mt-1">Please check the fileld form.</div> -->
    </div>
  </div>
  <!-- END: Failed Notification Content -->
</template>

<script>
import { defineComponent, toRefs, reactive, ref } from 'vue'
import { required, minLength, helpers } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import Toastify from 'toastify-js'
import adminApi from '@/services/admin-users/admin.api'

export default defineComponent({
  components: {},
  methods: {
    handleSave(data) {
      this.loading = true

      this.validate.$touch()
      if (this.validate.$invalid) {
        this.loading = false
        Toastify({
          node: cash('#failed-notification-content').clone().removeClass('hidden')[0],
          duration: 10000,
          newWindow: true,
          close: true,
          gravity: 'top',
          position: 'right',
          stopOnFocus: true
        }).showToast()
        return
      }

      const saveRequest = {
        name: data.target.elements.name.value,
        lastname: data.target.elements.lastname.value,
        password:
          data.target.elements.password.value === ''
            ? null
            : data.target.elements.password.value
      }

      adminApi.updateProfile(saveRequest).then(
        (response) => {
          Toastify({
            node: cash('#success-notification-content').clone().removeClass('hidden')[0],
            duration: 5000,
            newWindow: true,
            close: true,
            gravity: 'top',
            position: 'right',
            stopOnFocus: true
          }).showToast()
          this.admin = response.data
          this.formData.name = response.data.name
          this.formData.lastname = response.data.lastname
          this.formData.password = ''
          this.loading = false
        },
        (error) => {
          console.log(error)
          this.loading = false
          Toastify({
            node: cash('#failed-load-notification-content')
              .clone()
              .removeClass('hidden')[0],
            duration: 10000,
            newWindow: true,
            close: true,
            gravity: 'top',
            position: 'right',
            stopOnFocus: true
          }).showToast()
          this.loading = false
        }
      )
    }
  },
  computed: {
    console: () => console
  },
  data() {
    return {
      admin: {
        mail: ''
      }
    }
  },
  mounted() {
    adminApi.getProfile().then(
      (response) => {
        this.admin = response.data
        this.formData.name = response.data.name
        this.formData.lastname = response.data.lastname
        this.loading = false
      },
      (error) => {
        alert(error)
      }
    )
  },
  setup() {
    const loading = ref(true)
    const formData = reactive({
      name: '',
      lastname: '',
      password: '',
      active: false
    })
    const rules = {
      name: {
        required: helpers.withMessage('form.validation.personName.required', required),
        minLength: helpers.withMessage('form.validation.gte2ch', minLength(2))
      },
      lastname: {
        required: helpers.withMessage(
          'form.validation.personLastname.required',
          required
        ),
        minLength: helpers.withMessage('form.validation.gte2ch', minLength(2))
      },
      password: {
        minLength: helpers.withMessage('form.validation.gte6ch', minLength(6))
      },
      active: {}
    }
    const validate = useVuelidate(rules, toRefs(formData))

    return {
      loading,
      validate,
      formData
    }
  }
})
</script>
